import { useCallback, useEffect, useRef, useState } from 'react';
import { cabinet } from '../../api/cabinet';
import { isApiError, NETWORK_ERROR } from '../../api/client';
import type { ChatMessage } from '../../api/types';
import { CHAT_POLL_MS } from '../../config';

export interface LocalMessage extends ChatMessage {
  /** Only on messages typed here that the server has not confirmed yet. */
  local?: 'sending' | 'failed';
}

function byTime(a: ChatMessage, b: ChatMessage) {
  return Date.parse(a.sent_at) - Date.parse(b.sent_at) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

/** Add newly fetched messages to what we already have (dedupe by id, newer copy wins), old → new. */
export function unionMessages(have: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  if (!incoming.length) return have;
  const map = new Map(have.map((m) => [m.id, m]));
  for (const m of incoming) map.set(m.id, m);
  return [...map.values()].sort(byTime);
}

/** Server messages (sorted) first; pending local messages stay at the end until confirmed. */
export function mergeMessages(server: ChatMessage[], local: LocalMessage[]): LocalMessage[] {
  const ids = new Set(server.map((m) => m.id));
  return [...server.slice().sort(byTime), ...local.filter((m) => !ids.has(m.id))];
}

/** Messages written by the client. The conversation is stored from the company's side: inbound = client. */
export function isMine(m: ChatMessage): boolean {
  return m.direction === 'in' || m.direction === 'inbound';
}

export function useChat(caseId: string, opts: { pollMs?: number; api?: Pick<typeof cabinet, 'messages' | 'sendMessage'> } = {}) {
  const api = opts.api ?? cabinet;
  const pollMs = opts.pollMs ?? CHAT_POLL_MS;
  const [server, setServer] = useState<ChatMessage[]>([]);
  const [local, setLocal] = useState<LocalMessage[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [offline, setOffline] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const apiRef = useRef(api);
  apiRef.current = api;
  /** id of the newest server message: later polls ask only for what came after it (`?after=`). */
  const lastId = useRef<string | undefined>(undefined);

  useEffect(() => {
    lastId.current = undefined;
    setServer([]);
    setLocal([]);
    setLoaded(false);
  }, [caseId]);

  const fetchNow = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const list = await apiRef.current.messages(caseId, { signal, after: lastId.current });
        setServer((have) => {
          const next = unionMessages(have, list);
          lastId.current = next.at(-1)?.id ?? lastId.current;
          return next;
        });
        setLoaded(true);
        setOffline(false);
        setError(null);
      } catch (e) {
        if ((e as { name?: string })?.name === 'AbortError') return;
        if (isApiError(e) && e.code === NETWORK_ERROR) setOffline(true);
        else setError(e);
        setLoaded(true);
      }
    },
    [caseId],
  );

  // Poll every few seconds while the page is visible; one request at a time.
  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let ctrl: AbortController | undefined;
    const tick = async () => {
      if (stopped) return;
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
        ctrl = new AbortController();
        await fetchNow(ctrl.signal);
      }
      if (!stopped) timer = setTimeout(tick, pollMs);
    };
    void tick();
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        clearTimeout(timer);
        void tick();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stopped = true;
      clearTimeout(timer);
      ctrl?.abort();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fetchNow, pollMs]);

  const deliver = useCallback(
    async (tmp: LocalMessage) => {
      setLocal((l) => l.map((m) => (m.id === tmp.id ? { ...m, local: 'sending' } : m)));
      try {
        const saved = await apiRef.current.sendMessage(caseId, tmp.body);
        // not advancing lastId here: a manager reply may have arrived in between and must still be fetched
        if (saved && typeof saved === 'object' && saved.id) setServer((s) => unionMessages(s, [saved]));
        setLocal((l) => l.filter((m) => m.id !== tmp.id));
        if (!saved?.id) void fetchNow();
        return true;
      } catch {
        setLocal((l) => l.map((m) => (m.id === tmp.id ? { ...m, local: 'failed' } : m)));
        return false;
      }
    },
    [caseId, fetchNow],
  );

  const send = useCallback(
    (body: string) => {
      const text = body.trim();
      if (!text) return Promise.resolve(false);
      const tmp: LocalMessage = { id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`, direction: 'in', body: text, sent_at: new Date().toISOString(), local: 'sending' };
      setLocal((l) => [...l, tmp]);
      return deliver(tmp);
    },
    [deliver],
  );

  const resend = useCallback(
    (id: string) => {
      const m = local.find((x) => x.id === id);
      return m ? deliver(m) : Promise.resolve(false);
    },
    [local, deliver],
  );

  return { messages: mergeMessages(server, local), loaded, offline, error, send, resend, refresh: fetchNow };
}
